<template>
    <div>
        <general-order-details :return-url="returnUrl"
                               :create-order-url="createOrderUrl"
                               :edit-order-url-template="editOrderUrlTemplate"
                               :api-base-url="apiBaseUrl"
                               :file-download-url="fileDownloadUrl"
                               :current-user-account-id="currentUserAccountId"
                               :order="order"
                               :selectable-clients="selectableClients"
                               :initial-client-manages-assigned-contracts="initialClientManagesAssignedContracts"
                               :initial-client-branches="initialClientBranches"
                               :initial-ordering-client-contact="initialOrderingClientContact"
                               :initial-billing-contact="initialBillingContact"
                               :prompt-for-billing-contact="(order.clientOrderRequest == null && order.quote == null)"
                               :selectable-service-types="currentSelectableServiceTypes"
                               service-types-null-select-placeholder="Select a court/tribunal/other first"
                               :selectable-service-delays="currentSelectableServiceDelays"
                               :selectable-non-production-charge-types="currentSelectableNonProductionChargeTypes"
                               :possible-non-production-dates="null"
                               :non-production-charges="nonProductionCharges"
                               :attachments="attachments"
                               :all-attachment-classifications="allAttachmentClassifications"
                               :all-transcript-delivery-options="allTranscriptDeliveryOptions"
                               :default-delivery-contacts="currentDefaultDeliveryContacts"
                               :initial-delivery-contacts="initialDeliveryContacts"
                               :initial-delivery-cc-contacts="initialDeliveryCcContacts"
                               :initial-delivery-bcc-contacts="initialDeliveryBccContacts"
                               :delivery-contact-ids="deliveryContactIds"
                               :delivery-contact-cc-ids="deliveryContactCcIds"
                               :delivery-contact-bcc-ids="deliveryContactBccIds"
                               :emails-link="emailsLink"
                               :all-audio-sub-status-options="allAudioSubStatusOptions"
                               :all-log-notes-sub-status-options="allLogNotesSubStatusOptions"
                               :all-release-approval-sub-status-options="allReleaseApprovalSubStatusOptions"
                               :all-deposit-sub-status-options="allDepositSubStatusOptions"
                               :all-vetting-sub-status-options="allVettingSubStatusOptions"
                               :can-edit-order="canEditOrder"
                               :can-view-attachments="canViewAttachments"
                               :can-create-comment="canCreateComment"
                               :can-create-billing-information-comment="canCreateBillingInformationComment"
                               :can-edit-and-retract-all-comments="canEditAndRetractAllComments"
                               :can-view-sub-status-details="canViewSubStatusDetails"
                               :can-modify-audio-sub-status="canModifyAudioSubStatus"
                               :can-modify-log-notes-sub-status="canModifyLogNotesSubStatus"
                               :can-modify-release-approval-sub-status="canModifyReleaseApprovalSubStatus"
                               :can-modify-deposit-sub-status="canModifyDepositSubStatus"
                               :can-modify-vetting-sub-status="canModifyVettingSubStatus"
                               :hide-distribution-list="hideDistributionList"
                               :hide-default-distribution-list="hideDistributionList"
                               :readonly="readonly"
                               :validation-observer="validationObserver"
                               :save="saveOrder"
                               v-on:ordering-client-change="orderingClientChanged"
                               v-on:service-type-change="serviceTypeChanged"
                               :currency-code="currencyCode">
            <b-form-row>
                <validation-provider rules="required" name="Organisation" v-slot="vp" slim>
                    <b-form-group label="Organisation"
                                  label-for="interviewOrganisationId"
                                  class="col-md-4">
                        <select2 name="interviewOrganisationId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder || isUuidNullOrEmpty(orderingClientId)"
                                 v-model="interviewOrganisationId"
                                 :options="initialInterviewOrganisations"
                                 :enable-ajax="true"
                                 :placeholder="(orderingClientId == null) ? 'Select a client first' : 'Select an organisation'"
                                 key-property="interviewOrganisationId"
                                 text-property="name"
                                 :state="(orderingClientId == null) ? null : vp.errors[0] ? false : null"
                                 v-on:search="findInterviewOrganisations"
                                 v-on:select="interviewOrganisationSelected">
                        </select2>
                        <b-form-invalid-feedback :state="(orderingClientId == null) ? null : vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <div class="col-md-4"></div>
                <b-form-group label="Calculated Due Date / Time"
                              label-for="calculatedDueDateTimeUTC"
                              class="col-md-2"
                              v-if="calculatedDueDateTimeUTC != null">
                    <b-form-input name="calculatedDueDateTimeUTC"
                                  disabled
                                  :value="formatDateTime(calculatedDueDateTimeUTC, 'DD/MM/YYYY h:mm a')">
                    </b-form-input>
                </b-form-group>
                <b-form-group label="Manual Due Date / Time"
                              label-for="manuallyEnteredDueDateTimeUTC"
                              class="col-md-2"
                              v-if="calculatedDueDateTimeUTC != null">
                    <ejs-datetimepicker name="manuallyEnteredDueDateTimeUTC"
                                        v-model="manuallyEnteredDueDateTimeUTC"
                                        format="d/M/yyyy h:mma"
                                        :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"></ejs-datetimepicker>
                </b-form-group>
            </b-form-row>
            <b-form-row>
                <b-form-group label="EPIQ Reference"
                              label-for="interviewEpiqReference"
                              v-if="interview.epiqReference != 0"
                              class="col-md-2">
                    <b-input-group>
                        <b-form-input name="interviewEpiqReference"
                                      v-model="interview.epiqReferenceDisplayText"
                                      readonly>
                        </b-form-input>
                        <b-input-group-append v-if="interview && interview.interviewId">
                            <b-button variant="outline-secondary" title="Glossary" :href="$urlHelper.getPageUrl('ElectronicGlossary', [interview.interviewId])" target="_blank"><i class="fas fa-book mr-1"></i></b-button>
                        </b-input-group-append>
                    </b-input-group>

                </b-form-group>
                <validation-provider ref="interviewDateValidator" rules="required" name="Date/Time of Interview" v-slot="vp" slim>
                    <b-form-group label="Date/Time of Interview"
                                  label-for="interviewDateTime"
                                  class="col-md-4 col-xl-3">
                        <ejs-datetimepicker name="interviewDateTime"
                                            v-model="interview.interviewDateTime"
                                            format="d/M/yyyy h:mm:ssa"
                                            timeFormat="hh:mm:ssa"
                                            :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"></ejs-datetimepicker>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Time zone of Interview" v-slot="vp" slim>
                    <b-form-group label="Time zone of Interview"
                                  label-for="interviewTimezoneId"
                                  class="col-md-4 col-xl-3">
                        <select2 name="interviewTimezoneId"
                                 :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                 v-model="interview.interviewTimezoneId"
                                 :options="allTimeZones"
                                 placeholder="Select a timezone"
                                 :state="vp.errors[0] ? false : null">
                        </select2>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Length of Interview" v-slot="vp" slim>
                    <b-form-group label="Length"
                                  label-for="interviewLengthInSeconds"
                                  class="col-md-2">
                        <audio-length-input name="interviewLengthInSeconds"
                                            v-model="interview.lengthInSeconds"
                                            :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                            :state="vp.errors[0] ? false : null">
                        </audio-length-input>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <validation-provider rules="required" name="Location of Interview" v-slot="vp" slim>
                    <b-form-group label="Location of Interview"
                                  label-for="interviewLocation"
                                  class="col-md-6">
                        <b-form-input name="interviewLocation"
                                      :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                      v-model="interview.location"
                                      :state="vp.errors[0] ? false : null">
                        </b-form-input>
                        <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                    </b-form-group>
                </validation-provider>
                <b-form-group label="Title of related Matter or Case"
                              label-for="interviewMatterOrCaseReference"
                              class="col-md-6">
                    <b-form-input name="interviewMatterOrCaseReference"
                                  :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                                  v-model="interview.matterOrCaseReference">
                    </b-form-input>
                </b-form-group>
            </b-form-row>
            <interview-parties title="Interviewer/s"
                               edit-dialog-title="Interviewer Details"
                               :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                               v-model="interview.interviewers"
                               class="mb-2">
            </interview-parties>
            <interview-parties title="Interviewee/s"
                               edit-dialog-title="Interviewee Details"
                               :disabled="readonly || (order.confirmedDateTimeUTC != null) || !canEditOrder"
                               v-model="interview.interviewees"
                               class="mb-2">
            </interview-parties>

            <template v-slot:after-service-selection>
                <b-form-row v-if="serviceTypeClientProvidesAudio">
                    <validation-provider rules="required" name="Audio Supplied As" v-slot="vp" slim>
                        <b-form-group label="Audio Supplied As"
                                      label-for="audioSupplyMethod"
                                      class="col-md-4">
                            <select2 name="audioSupplyMethod"
                                     placeholder="Select how audio will be supplied"
                                     v-model="audioSupplyMethod"
                                     :disabled="readonly || !canEditOrder"
                                     :options="allAudioSupplyMethods"
                                     disable-search
                                     :state="vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="Audio Supplied Details" v-slot="vp" slim v-if="(audioSupplyMethod == 'Other')">
                        <b-form-group label="Audio Supplied Details"
                                      label-for="audioSupplyMethodDetails"
                                      class="col-md-8">
                            <b-form-input name="audioSupplyMethodDetails"
                                          :disabled="readonly || !canEditOrder"
                                          v-model="audioSupplyMethodDetails"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
                <b-form-row v-if="serviceTypeClientProvidesAudio">
                    <validation-provider rules="required" name="Audio Received At" v-slot="vp" slim>
                        <b-form-group label="Audio Received At"
                                      label-for="audioReceivedDateTimeUTC"
                                      class="col-md-2">
                            <ejs-datetimepicker name="audioReceivedDateTimeUTC"
                                                v-model="audioReceivedDateTimeUTC"
                                                format="d/M/yyyy h:mma"
                                                :disabled="readonly || !canEditOrder">
                            </ejs-datetimepicker>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider rules="required" name="Audio Received By" v-slot="vp" slim>
                        <b-form-group label="Audio Received By"
                                      label-for="audioReceivedBy"
                                      class="col-md-8">
                            <b-form-input name="audioReceivedBy"
                                          :disabled="readonly || !canEditOrder"
                                          v-model="audioReceivedBy"
                                          :state="vp.errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <validation-provider v-if="$config.enableAudioQualityOptions"
                                         rules="required" name="Audio Quality" v-slot="vp" slim>
                        <b-form-group v-if="$config.enableAudioQualityOptions"
                                      label="Audio Quality"
                                      label-for="audioQuality"
                                      class="col-md-2">
                            <select2 name="audioQuality"
                                     placeholder="Select audio quality"
                                     v-model="audioQuality"
                                     :disabled="readonly || !canEditOrder"
                                     :options="allAudioQualities"
                                     disable-search
                                     :state="vp.errors[0] ? false : null">
                            </select2>
                            <b-form-invalid-feedback :state="vp.errors[0] ? false : null">{{ vp.errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                </b-form-row>
            </template>

        </general-order-details>

    </div>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';
    import { DatePickerPlugin, DateTimePickerPlugin } from '@syncfusion/ej2-vue-calendars';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';

    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);

    import AudioLengthInput from '../AudioLengthInput';
    import Select2 from '../Select2';
    import ErrorsMixin from '../../mixins/ErrorsMixin';
    import DateTimeMixin from '../../mixins/DateTimeMixin';
    import UuidMixins from '../../mixins/UuidMixins';

    import GeneralOrderDetails from './GeneralOrderDetails.vue'

    Vue.use(BootstrapVue);
    Vue.use(DatePickerPlugin);
    Vue.use(DateTimePickerPlugin);
    Vue.component('audio-length-input', AudioLengthInput);
    Vue.component('select2', Select2);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    Vue.component('general-order-details', GeneralOrderDetails);

    export default {
        name: 'InterviewOrderDetails',
        mixins: [ErrorsMixin, DateTimeMixin, UuidMixins],
        props: {
            returnUrl: {
                type: String,
                required: true
            },
            createOrderUrl: {
                type: String,
                required: true
            },
            editOrderUrlTemplate: {
                type: String,
                required: true
            },
            apiBaseUrl: {
                type: String,
                required: true
            },
            fileDownloadUrl: {
                type: String,
                required: true
            },
            currentUserAccountId: {
                type: String,
                required: true
            },
            order: {
                type: Object,
                required: true
            },
            selectableClients: {
                type: Array,
                default: null
            },
            initialClientManagesAssignedContracts: {
                type: Boolean,
                default: false
            },
            initialClientBranches: {
                type: Array,
                default: null
            },
            initialOrderingClientContact: {
                type: Object,
                default: null
            },
            initialBillingContact: {
                type: Object,
                default: null
            },
            initialServiceTypes: {
                type: Array,
                default: null
            },
            initialServiceDelays: {
                type: Array,
                default: null
            },
            initialNonProductionChargeTypes: {
                type: Array,
                default: null
            },
            nonProductionCharges: {
                type: Array,
                default: null
            },
            initialDeliveryContacts: {
                type: Array,
                default: null
            },
            initialDeliveryCcContacts: {
                type: Array,
                default: null
            },
            initialDeliveryBccContacts: {
                type: Array,
                default: null
            },
            deliveryContactIds: {
                type: Array,
                default: null
            },
            deliveryContactCcIds: {
                type: Array,
                default: null
            },
            deliveryContactBccIds: {
                type: Array,
                default: null
            },
            emailsLink: {
                type: String,
                default: null
            },
            allAttachmentClassifications: {
                type: Array,
                default: null
            },
            allTranscriptDeliveryOptions: {
                type: Array,
                default: null
            },
            attachments: {
                type: Array,
                default: null
            },
            allAudioSubStatusOptions: {
                type: Array,
                default: null
            },
            allLogNotesSubStatusOptions: {
                type: Array,
                default: null
            },
            allReleaseApprovalSubStatusOptions: {
                type: Array,
                default: null
            },
            allDepositSubStatusOptions: {
                type: Array,
                default: null
            },
            allVettingSubStatusOptions: {
                type: Array,
                default: null
            },
            canEditOrder: {
                type: Boolean,
                requird: true
            },
            canViewAttachments: {
                type: Boolean,
                requird: true
            },
            canCreateComment: {
                type: Boolean,
                default: false
            },
            canCreateBillingInformationComment: {
                type: Boolean,
                default: false
            },
            canEditAndRetractAllComments: {
                type: Boolean,
                default: false
            },
            canViewSubStatusDetails: {
                type: Boolean,
                default: false
            },
            canModifyAudioSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyLogNotesSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyReleaseApprovalSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyDepositSubStatus: {
                type: Boolean,
                default: false
            },
            canModifyVettingSubStatus: {
                type: Boolean,
                default: false
            },
            readonly: {
                type: Boolean,
                default: false
            },

            initialInterviewOrganisation: {
                type: Object,
                default: null
            },
            allTimeZones: {
                type: Array,
                default: null
            },
            defaultTimezoneId: {
                type: String,
                default: null
            },
            allAudioSupplyMethods: {
                type: Array,
                default: null
            },
            allAudioQualities: {
                type: Array,
                default: null
            },
            currencyCode: {
                type: String,
                required: true
            },
        },
        data: function () {
            return {
                orderingClientId: this.isUuidNullOrEmpty(this.order.orderingClientId) ? null : this.order.orderingClientId,
                isExempt: this.order.isExempt,
                currentSelectableServiceTypes: this.initialServiceTypes,
                currentSelectableServiceDelays: this.initialServiceDelays,
                currentSelectableNonProductionChargeTypes: this.initialNonProductionChargeTypes,
                currentDefaultDeliveryContacts: this.defaultDeliveryContacts,
                initialInterviewOrganisations: (this.initialInterviewOrganisation == null) ? null : [this.initialInterviewOrganisation],
                interviewOrganisationId: this.isUuidNullOrEmpty(this.order.interviewOrganisationId) ? null : this.order.interviewOrganisationId,
                interview: {
                    interviewId: (this.order.interview != null) ? this.order.interview.interviewId : null,
                    epiqReference: (this.order.interview != null) ? this.order.interview.epiqReference : null,
                    epiqReferenceDisplayText: (this.order.interview != null) ? this.order.interview.epiqReferenceDisplayText : null,
                    interviewDateTime: (this.order.interview != null) ? this.parseDateTime(this.order.interview.interviewDateTime, null, false, true) : null,
                    interviewTimezoneId: (this.order.interview?.interviewTimezoneId == null) ? this.defaultTimezoneId : this.order.interview.interviewTimezoneId,
                    lengthInSeconds: (this.order.interview != null) ? this.order.interview.lengthInSeconds : null,
                    location: (this.order.interview != null) ? this.order.interview.location : null,
                    matterOrCaseReference: (this.order.interview != null) ? this.order.interview.matterOrCaseReference : null,
                    interviewers: (this.order.interview != null) ? this.order.interview.interviewers : null,
                    interviewees: (this.order.interview != null) ? this.order.interview.interviewees : null
                },
                serviceTypeClientProvidesAudio: (this.order.serviceType == null) ? false : this.order.serviceType.clientProvidesAudio,
                audioSupplyMethod: this.order.audioSupplyMethod ? this.order.audioSupplyMethodString : null,
                audioSupplyMethodDetails: this.order.audioSupplyMethodDetails,
                audioReceivedBy: this.order.audioReceivedBy,
                audioQuality: this.order.audioQuality ? this.order.audioQualityString : null,
            
                audioReceivedDateTimeUTC: (this.order.audioReceivedDateTimeUTC != null) ? this.parseDateTime(this.order.audioReceivedDateTimeUTC) : null,

                calculatedDueDateTimeUTC: (this.order.calculatedDueDateTimeUTC != null) ? this.parseDateTime(this.order.calculatedDueDateTimeUTC, null, false, false) : null,
                manuallyEnteredDueDateTimeUTC: (this.order.manuallyEnteredDueDateTimeUTC != null) ? this.parseDateTime(this.order.manuallyEnteredDueDateTimeUTC) : null,
                hideDistributionList: false,
            };
        },
        computed: {
            validationObserver: function () {
                return this.$refs.validationObserver;
            }
        },
        watch: {
            interviewOrganisationId: function () {
                this.reloadServiceTypes();
                this.reloadServiceDelays();
                this.reloadNonProductionChargeTypes();

                Vue.nextTick().then(() => {
                    this.$refs.interviewDateValidator.reset();
                });
            }
        },
        methods: {
            apiControllerUrl: function (apiRelativeUrl) {
                var url = this.apiBaseUrl;
                if (!url.endsWith('/')) url += '/';
                url += apiRelativeUrl;
                return url;
            },
            orderingClientChanged: function (newOrderingClientId) {
                this.orderingClientId = newOrderingClientId;
            },
            findInterviewOrganisations: function (params, success, failure) {
                var filter = {
                    searchTerm: ''
                };
                if (typeof (params.data.term) != "undefined")
                    filter.searchTerm = params.data.term;
                var url = this.$urlHelper.getApiUrl('InterviewOrganisation', 'byclient', [this.orderingClientId, 'find']);
                axios
                    .post(url, JSON.stringify(filter),
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(function (response) {
                        var mappedData = response.data;
                        if (mappedData != null) {
                            mappedData = $.map(mappedData, function (obj) {
                                obj.id = obj.interviewOrganisationId;
                                obj.text = obj.name;
                                return obj;
                            });
                        }
                        success({ results: mappedData });
                    })
                    .catch(function (error) {
                        failure(error);
                    });
            },
            interviewOrganisationSelected: function (interviewOrganisation) {

                if (interviewOrganisation != null) {
                    this.hideDistributionList = interviewOrganisation.hideDistributionList;
                }
                else {
                    this.hideDistributionList = false;
                }
            },
            reloadServiceTypes: function () {
                if (this.interviewOrganisationId) {
                    var url = this.apiControllerUrl('servicetype');
                    url += '/byintervieworganisation/' + this.interviewOrganisationId ;
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceTypes = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceTypes = null;
                }
            },
            reloadServiceDelays: function () {
                if (this.interviewOrganisationId) {
                    var url = this.apiControllerUrl('servicedelay');
                    url += '/byintervieworganisation/' + this.interviewOrganisationId;
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableServiceDelays = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableServiceDelays = null;
                }

            },
            reloadNonProductionChargeTypes: function () {
                if (this.interviewOrganisationId) {
                    var url = this.apiControllerUrl('nonproductionchargetype');
                    url += '/byintervieworganisation/' + this.interviewOrganisationId + '/rates/true';
                    axios
                        .get(url)
                        .then((response) => {
                            this.currentSelectableNonProductionChargeTypes = response.data;
                        })
                        .catch((ex) => {
                            var errors = this.handleAxiosException(ex);
                            this.showErrorsToast(errors);
                        });
                } else {
                    this.currentSelectableNonProductionChargeTypes = null;
                }
            },
            serviceTypeChanged: function (serviceType) {
                if (serviceType != null) {
                    this.serviceTypeClientProvidesAudio = serviceType.clientProvidesAudio;
                }
                else {
                    this.serviceTypeClientProvidesAudio = false;
                }
            },
            saveOrder: function (generalData) {
                var data = {
                    ...generalData,
                    interview: {
                        interviewOrganisationId: this.interviewOrganisationId,
                        //this is required to remove the time offset from the interview date/time, the time should be displayed and treated as local to wherever the interview is
                        interviewDateTime: this.parseDateTime(this.formatDateTime(this.interview.interviewDateTime, 'YYYY-MM-DDTHH:mm:ss')), 
                        interviewTimezoneId: this.interview.interviewTimezoneId,
                        location: this.interview.location,
                        matterOrCaseReference: this.interview.matterOrCaseReference,
                        interviewers: this.interview.interviewers,
                        interviewees: this.interview.interviewees,
                    }
                };

                data.order.quoteClientIdToCopy = !this.isUuidNullOrEmpty(this.order?.quoteClientIdToCopy) ? this.order.quoteClientIdToCopy : null;
                data.order.interviewOrganisationId = this.interviewOrganisationId;
                if (this.interview.interviewId != null) data.order.interviewId = this.interview.interviewId;
                if (this.interview.lengthInSeconds) data.interview.lengthInSeconds = this.interview.lengthInSeconds;
                data.order.audioSupplyMethod = this.audioSupplyMethod;
                data.order.audioSupplyMethodDetails = this.audioSupplyMethodDetails;
                data.order.audioReceivedDateTimeUTC = this.audioReceivedDateTimeUTC;
                data.order.audioReceivedBy = this.audioReceivedBy;
                data.order.audioQuality = this.audioQuality;
                data.order.manuallyEnteredDueDateTimeUTC = this.manuallyEnteredDueDateTimeUTC;
                var url = this.apiControllerUrl('order');

                url += '/interview';
                return axios
                    .request(
                        {
                            method: this.isUuidNullOrEmpty(this.order.orderId) ? 'post' : 'put',
                            url: url,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        });
            },            
        },
        mounted: function () {
            if (this.interview.interviewDateTime != null) this.interview.interviewDateTime = this.parseDateTime(this.interview.interviewDateTime, null, false, true); //this is required to remove the time offset from the interview date/time, the time should be displayed and treated as local to wherever the interview is
            if (this.audioReceivedDateTimeUTC != null) this.audioReceivedDateTimeUTC = this.parseDateTime(this.audioReceivedDateTimeUTC);
            if (this.manuallyEnteredDueDateTimeUTC != null) this.manuallyEnteredDueDateTimeUTC = this.parseDateTime(this.manuallyEnteredDueDateTimeUTC);
            
            Vue.nextTick().then(() => {
                this.$refs.interviewDateValidator.reset();
            });
        }
    }
</script>
